/**
 * Wire types shared with the backend's reports API.
 *
 * All money is a **string of minor units**: JSON numbers lose precision past 2^53, and the admin
 * never does money arithmetic — it formats what the backend already computed (CLAUDE.md §6).
 */

export type AgingBucket = 'CURRENT' | 'D1_30' | 'D31_60' | 'D61_90' | 'D90_PLUS';

export const AGING_BUCKETS: AgingBucket[] = ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'D90_PLUS'];

export interface CompanyAgingRow {
  companyId: string;
  name: string;
  openBalanceCents: string;
  buckets: Record<AgingBucket, string>;
  overdueCents: string;
  limitCents: string | null;
  enforce: 'BLOCK' | 'OFF';
}

export type PlanName = 'FREE' | 'STARTER' | 'GROWTH' | 'SCALE';

export interface PlanTier {
  plan: PlanName;
  handle: string;
  monthlyPriceUsd: number;
  /** `null` = unlimited. */
  activeCompanies: number | null;
  current: boolean;
  includes: {
    automatedDunning: boolean;
    statements: boolean;
    agingExports: boolean;
    buyerPortal: boolean;
    manualInvoices: boolean;
  };
}

export interface PlanStatus {
  plan: PlanName;
  /** `null` = unlimited. */
  capacity: number | null;
  used: number;
  withinQuota: string[];
  /** True at 80% of capacity — the merchant is warned before anything is blocked (SPEC N5). */
  nearingLimit: boolean;
  tiers: PlanTier[];
}

export interface ShopSettings {
  /** Off until the merchant turns it on — nothing is emailed to their buyers before that. */
  dunningEnabled: boolean;
  statementDay: number;
}

export interface VerificationStatus {
  /** ISO timestamp of the last clean reconciliation run, or null if none has passed yet. */
  lastVerifiedAt: string | null;
  openDiscrepancies: number;
}

export interface DashboardReport {
  asOf: string;
  currency: string;
  totals: {
    outstandingCents: string;
    overdueCents: string;
    currentCents: string;
  };
  bucketLabels: Record<AgingBucket, string>;
  companies: CompanyAgingRow[];
  plan: PlanStatus;
  settings: ShopSettings;
  verification: VerificationStatus;
}

export interface InvoiceRow {
  id: string;
  orderName: string;
  issuedAt: string;
  dueAt: string | null;
  termsName: string | null;
  originalTotalCents: string;
  openCents: string;
  status: 'OPEN' | 'PAID' | 'VOID' | 'CREDIT';
  bucket: AgingBucket | null;
}

export interface BackfillResult {
  scanned: number;
  ingested: number;
  entriesInserted: number;
  pages: number;
}
